import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {first} from 'rxjs/operators';
import {ConfigurationService} from '../configuration.service';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {MatSelectChange} from '@angular/material/select';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {validateInteger} from '../../commons/validators';
import {isDefined} from '../../commons/commons';
import {I18NDateTimePipe} from '../../i18n/i18n.pipe';
import {I18NService} from '../../i18n/i18n.service';
import {AuthService} from 'auth-commons';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {DATE_FORMATS} from '../../i18n/i18n-defaults';
import {Subject} from 'rxjs';
import {ActivatedRoute} from '@angular/router';

export enum TimeFilterProfile {
    MEASURES,
    FORECAST_MEASURES,
    EC_ECONOMICS,
    FORECAST,
    PLANNING,
    NOTIFICATION
}

export enum TimeFilterType {
    CUSTOM = 'custom',
    DAY_BEFORE_YESTERDAY = 'day_before_yesterday',
    LAST_12_HOURS = 'last_12_hours',
    LAST_14_DAYS = 'last_14_days',
    LAST_15_MINUTES = 'last_15_minutes',
    LAST_24_HOURS = 'last_24_hours',
    LAST_2_DAYS = 'last_2_days',
    LAST_30_DAYS = 'last_30_days',
    LAST_30_MINUTES = 'last_30_minutes',
    LAST_3_HOURS = 'last_3_hours',
    LAST_3_MONTHS = 'last_3_months',
    LAST_6_HOURS = 'last_6_hours',
    LAST_6_MONTHS = 'last_6_months',
    LAST_7_DAYS = 'last_7_days',
    LAST_90_DAYS = 'last_90_days',
    LAST_HOUR = 'last_hour',
    LAST_MONTH = 'last_month',
    LAST_YEAR = 'last_year',
    NEXT_24_HOURS = 'next_24_hours',
    NEXT_3_DAYS = 'next_3_days',
    PREVIOUS_MONTH = 'previous_month',
    PREVIOUS_WEEK = 'previous_week',
    RANGE = 'range',
    SINGLE_MONTH = 'single_month',
    THIS_DAY_LAST_WEEK = 'this_day_last_week',
    THIS_MONTH = 'this_month', // "this month so far"
    THIS_WEEK = 'this_week', // "this week so far"
    THIS_YEAR = 'this_year', // "this year so far"
    TODAY = 'today', // "today so far"
    TOMORROW = 'tomorrow',
    YESTERDAY = 'yesterday',
}

export interface TimeRange {
    fromTime: number; // epoch in milliseconds
    toTime: number; // epoch in milliseconds
    includesNow: boolean; // whether this range is something like "Last ... or ... so far" ("hourly" ranges do *not* include now by definition)
}

export interface TimeFilter {
    type: TimeFilterType;
    duration: number; // in minutes; in case of "variable" ranges (e.g. "this week so far"), this represents *maximum* duration
    range: () => TimeRange;
}

export interface DateBoundaries {
    minDateTime: Moment; // inclusive
    maxDateTime: Moment; // inclusive
    delta?: number;
}

type TimeFilterFactory = (timeZone?: string, hourly?: boolean) => TimeFilter;

interface TimeFilterSettings {
    name: string;
    factory: TimeFilterFactory;
}

interface TimeFilterProfileSettings {
    types: TimeFilterType[];
    defaultType: TimeFilterType;
    boundaryFactory: (timeZone?: string) => DateBoundaries;
    hourlyRanges: boolean; // days/hours ranges are to be hourly-aligned
}

const SETTINGS: { [filterType in TimeFilterType]?: TimeFilterSettings } = {
    [TimeFilterType.LAST_15_MINUTES]: {
        name: 'Last 15 minutes',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'minutes', 15, TimeFilterType.LAST_15_MINUTES),
    },
    [TimeFilterType.LAST_30_MINUTES]: {
        name: 'Last 30 minutes',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'minutes', 30, TimeFilterType.LAST_30_MINUTES),
    },
    [TimeFilterType.LAST_HOUR]: {
        name: 'Last hour',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'hours', 1, TimeFilterType.LAST_HOUR),
    },
    [TimeFilterType.LAST_3_HOURS]: {
        name: 'Last 3 hours',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'hours', 3, TimeFilterType.LAST_3_HOURS),
    },
    [TimeFilterType.LAST_6_HOURS]: {
        name: 'Last 6 hours',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'hours', 6, TimeFilterType.LAST_6_HOURS),
    },
    [TimeFilterType.LAST_12_HOURS]: {
        name: 'Last 12 hours',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'hours', 12, TimeFilterType.LAST_12_HOURS),
    },
    [TimeFilterType.LAST_24_HOURS]: {
        name: 'Last 24 hours',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'hours', 24, TimeFilterType.LAST_24_HOURS),
    },
    [TimeFilterType.LAST_2_DAYS]: {
        name: 'Last 2 days',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'days', 2, TimeFilterType.LAST_2_DAYS),
    },
    [TimeFilterType.LAST_7_DAYS]: {
        name: 'Last 7 days',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'days', 7, TimeFilterType.LAST_7_DAYS),
    },
    [TimeFilterType.LAST_14_DAYS]: {
        name: 'Last 14 days',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'days', 14, TimeFilterType.LAST_14_DAYS),
    },
    [TimeFilterType.LAST_30_DAYS]: {
        name: 'Last 30 days',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'days', 30, TimeFilterType.LAST_30_DAYS),
    },
    [TimeFilterType.LAST_90_DAYS]: {
        name: 'Last 90 days',
        factory: (timeZone, hourly) => mkLastUnitFilter(timeZone, !!hourly, 'days', 90, TimeFilterType.LAST_90_DAYS),
    },
    [TimeFilterType.LAST_YEAR]: {
        name: 'Last year',
        factory: (timeZone) => mkPreviousYearFilter(timeZone),
    },
    [TimeFilterType.LAST_MONTH]: {
        name: 'Last month',
        factory: (timeZone) => mkPreviousMonthFilter(timeZone, TimeFilterType.LAST_MONTH),
    },
    [TimeFilterType.LAST_3_MONTHS]: {
        name: 'Last 3 months',
        factory: (timeZone) => mkPreviousMonthsFilter(timeZone, 3, TimeFilterType.LAST_3_MONTHS),
    },
    [TimeFilterType.LAST_6_MONTHS]: {
        name: 'Last 6 months',
        factory: (timeZone) => mkPreviousMonthsFilter(timeZone, 6, TimeFilterType.LAST_6_MONTHS),
    },
    [TimeFilterType.YESTERDAY]: {
        name: 'Yesterday',
        factory: (timeZone) => mkYesterdayFilter(timeZone),
    },
    [TimeFilterType.DAY_BEFORE_YESTERDAY]: {
        name: 'Day before yesterday',
        factory: (timeZone) => mkDayBeforeYesterdayFilter(timeZone),
    },
    [TimeFilterType.THIS_DAY_LAST_WEEK]: {
        name: 'This day last week',
        factory: (timeZone) => mkThisDayLastWeekFilter(timeZone),
    },
    [TimeFilterType.PREVIOUS_WEEK]: {
        name: 'Previous week',
        factory: (timeZone) => mkPreviousWeekFilter(timeZone),
    },
    [TimeFilterType.PREVIOUS_MONTH]: {
        name: 'Previous month',
        factory: (timeZone) => mkPreviousMonthFilter(timeZone, TimeFilterType.PREVIOUS_MONTH),
    },
    [TimeFilterType.TODAY]: {
        name: 'Today', // "today so far"
        factory: (timeZone, hourly) => mkThisUnitSoFarFilter(timeZone, !!hourly, 'day', TimeFilterType.TODAY),
    },
    [TimeFilterType.THIS_WEEK]: {
        name: 'This week', // "this week so far"
        factory: (timeZone, hourly) => mkThisUnitSoFarFilter(timeZone, !!hourly, 'isoWeek', TimeFilterType.THIS_WEEK),
    },
    [TimeFilterType.THIS_MONTH]: {
        name: 'Current month', // "this month so far"
        factory: (timeZone, hourly) => mkThisUnitSoFarFilter(timeZone, !!hourly, 'month', TimeFilterType.THIS_MONTH),
    },
    [TimeFilterType.SINGLE_MONTH]: {
        name: 'Single month',
        factory: (timeZone, hourly) => mkThisUnitSoFarFilter(timeZone, !!hourly, 'month', TimeFilterType.SINGLE_MONTH),
    },
    [TimeFilterType.THIS_YEAR]: {
        name: 'Current year', // "this whole year"
        factory: (timeZone, hourly) => mkWholeYear(timeZone, !!hourly, 'year', TimeFilterType.THIS_YEAR),
    },
    [TimeFilterType.NEXT_24_HOURS]: {
        name: 'Next 24 hours',
        factory: (timeZone) => mkNextUnitFilter(timeZone, 'hours', 24, TimeFilterType.NEXT_24_HOURS),
    },
    [TimeFilterType.NEXT_3_DAYS]: {
        name: 'Next 3 days',
        factory: (timeZone) => mkNextUnitFilter(timeZone, 'days', 3, TimeFilterType.NEXT_3_DAYS),
    },
    [TimeFilterType.TOMORROW]: {
        name: 'Tomorrow',
        factory: (timeZone) => mkTomorrowFilter(timeZone),
    },
    [TimeFilterType.CUSTOM]: {
        name: 'Custom',
        factory: () => ({type: TimeFilterType.CUSTOM, duration: null, range: null, includesNow: false} as TimeFilter) // handled separately
    },
    [TimeFilterType.RANGE]: {
        name: 'Customized multi-month',
        factory: () => ({
            type: TimeFilterType.RANGE,
            duration: null,
            range: () => null,
            includesNow: false
        } as TimeFilter) // handled separately
    }
};

const PROFILE_SETTINGS: { [profile in TimeFilterProfile]?: TimeFilterProfileSettings } = {
    [TimeFilterProfile.MEASURES]: {
        types: [
            TimeFilterType.LAST_HOUR,
            TimeFilterType.LAST_3_HOURS,
            TimeFilterType.LAST_6_HOURS,
            TimeFilterType.LAST_12_HOURS,
            TimeFilterType.LAST_24_HOURS,
            TimeFilterType.LAST_2_DAYS,
            TimeFilterType.LAST_7_DAYS,
            TimeFilterType.LAST_30_DAYS,
            TimeFilterType.LAST_90_DAYS,
            TimeFilterType.YESTERDAY,
            TimeFilterType.DAY_BEFORE_YESTERDAY,
            TimeFilterType.THIS_DAY_LAST_WEEK,
            TimeFilterType.PREVIOUS_WEEK,
            TimeFilterType.PREVIOUS_MONTH,
            TimeFilterType.TODAY,
            TimeFilterType.THIS_WEEK,
            TimeFilterType.THIS_MONTH,
            TimeFilterType.CUSTOM
        ],
        defaultType: TimeFilterType.LAST_24_HOURS,
        boundaryFactory: timeZone => mkDaysBoundaries(timeZone, 90, null, false),
        hourlyRanges: false
    },
    [TimeFilterProfile.FORECAST_MEASURES]: {
        types: [
            TimeFilterType.LAST_HOUR,
            TimeFilterType.LAST_3_HOURS,
            TimeFilterType.LAST_6_HOURS,
            TimeFilterType.LAST_12_HOURS,
            TimeFilterType.LAST_24_HOURS,
            TimeFilterType.LAST_2_DAYS,
            TimeFilterType.LAST_7_DAYS,
            TimeFilterType.LAST_30_DAYS,
            TimeFilterType.LAST_90_DAYS,
            TimeFilterType.YESTERDAY,
            TimeFilterType.DAY_BEFORE_YESTERDAY,
            TimeFilterType.THIS_DAY_LAST_WEEK,
            TimeFilterType.PREVIOUS_WEEK,
            TimeFilterType.PREVIOUS_MONTH,
            TimeFilterType.TODAY,
            TimeFilterType.THIS_WEEK,
            TimeFilterType.THIS_MONTH,
            TimeFilterType.NEXT_24_HOURS,
            TimeFilterType.NEXT_3_DAYS,
            TimeFilterType.TOMORROW,
            TimeFilterType.CUSTOM
        ],
        defaultType: TimeFilterType.NEXT_24_HOURS,
        boundaryFactory: timeZone => mkDaysBoundaries(timeZone, 90, 3, false),
        hourlyRanges: false
    },
    [TimeFilterProfile.EC_ECONOMICS]: {
        types: [
            TimeFilterType.RANGE,
            TimeFilterType.LAST_3_MONTHS,
            TimeFilterType.LAST_6_MONTHS,
            TimeFilterType.LAST_MONTH,
            TimeFilterType.LAST_YEAR,
            TimeFilterType.SINGLE_MONTH,
            TimeFilterType.THIS_MONTH,
            TimeFilterType.THIS_YEAR,
        ],
        defaultType: TimeFilterType.THIS_YEAR,
        boundaryFactory: timeZone => mkWholeYearBoundaries(timeZone, true),
        hourlyRanges: true
    },
    [TimeFilterProfile.FORECAST]: {
        types: [
            TimeFilterType.LAST_6_HOURS,
            TimeFilterType.LAST_12_HOURS,
            TimeFilterType.LAST_24_HOURS,
            TimeFilterType.LAST_2_DAYS,
            TimeFilterType.LAST_7_DAYS,
            TimeFilterType.LAST_30_DAYS,
        ],
        defaultType: TimeFilterType.LAST_24_HOURS,
        boundaryFactory: timeZone => mkDaysBoundaries(timeZone, 90, null, false),
        hourlyRanges: false
    },
    [TimeFilterProfile.PLANNING]: {
        types: [
            TimeFilterType.LAST_6_HOURS,
            TimeFilterType.LAST_12_HOURS,
            TimeFilterType.LAST_24_HOURS,
            TimeFilterType.LAST_2_DAYS,
            TimeFilterType.LAST_7_DAYS,
            TimeFilterType.LAST_30_DAYS,
        ],
        defaultType: TimeFilterType.LAST_24_HOURS,
        boundaryFactory: timeZone => mkDaysBoundaries(timeZone, 90, null, false),
        hourlyRanges: false
    },
    [TimeFilterProfile.NOTIFICATION]: {
        types: [
            TimeFilterType.LAST_15_MINUTES,
            TimeFilterType.LAST_30_MINUTES,
            TimeFilterType.LAST_HOUR,
            TimeFilterType.LAST_3_HOURS,
            TimeFilterType.LAST_6_HOURS,
            TimeFilterType.LAST_12_HOURS,
            TimeFilterType.LAST_24_HOURS,
            TimeFilterType.LAST_2_DAYS,
            TimeFilterType.LAST_7_DAYS,
            TimeFilterType.LAST_30_DAYS,
            TimeFilterType.CUSTOM
        ],
        defaultType: TimeFilterType.LAST_24_HOURS,
        boundaryFactory: timeZone => mkDaysBoundaries(timeZone, 30, null, false),
        hourlyRanges: false
    }
};

const CUSTOM_DIALOG_MIN_WIDTH = 800;

@Component({
    selector: 'app-time-filter',
    templateUrl: './time-filter.component.html',
    styleUrls: ['./time-filter.component.scss'],
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS}
    ]
})
export class TimeFilterComponent implements OnInit, OnChanges {

    private dtp: I18NDateTimePipe;
    private timeZone: string;
    @ViewChild('customRange') private customRangeDialog: TemplateRef<any>;
    private customDialogRef: MatDialogRef<any, [() => TimeRange, number, string]>;

    timeFilters: TimeFilter[];
    timeFilter: TimeFilter;
    customTimeRangeForm: FormGroup;
    customName: string;

    @Input() profile: TimeFilterProfile; // mandatory
    @Input() allowedTypes: TimeFilterType[]; // optional
    @Input() defaultType: TimeFilterType; // optional (CUSTOM not allowed, as it would be impossible to specify actual range)
    @Input() boundaries: DateBoundaries; // optional
    @Input() keepCustomRange: boolean; // optional, default true
    @Input() disabledTypes: TimeFilterType[]; // optional, list of types to show disabled
    @Input() setType: Subject<TimeFilterType>; // optional, set manually a filter type
    @Input() isKeepPreviousSelectionEnabled: boolean;
    @Output() timeFilterChange: EventEmitter<TimeFilter> = new EventEmitter<TimeFilter>();
    keepPreviousSelection = true;

    // exported in HTML
    TimeFilterType = TimeFilterType;
    TimeFilterProfile = TimeFilterProfile;

    constructor(private readonly configurationService: ConfigurationService,
                private readonly dialog: MatDialog,
                private readonly i18n: I18NService,
                private readonly auth: AuthService,
                private fb: FormBuilder,
                private readonly route: ActivatedRoute) {
        this.dtp = new I18NDateTimePipe(i18n, auth);
        this.timeZone = null;
        this.timeFilters = [];
        this.customTimeRangeForm = this.fb.group({
            startDate: new FormControl(null, [Validators.required]),
            startHour: new FormControl(null, Validators.compose([Validators.required, validateInteger, Validators.min(0), Validators.max(23)])),
            endDate: new FormControl(null, [Validators.required]),
            endHour: new FormControl(null, Validators.compose([Validators.required, validateInteger, Validators.min(0), Validators.max(23)]))
        }, {
            validators: [ctrl => this.validateCustomTimeRange(ctrl)]
        });
        this.customName = null;
        this.customDialogRef = null;
    }

    ngOnInit() {
        this.setType?.asObservable()
            .subscribe(value => {
                const filter = this.timeFilters.find((tf => tf.type === value));
                if (filter) {
                    this.timeFilter = filter;
                }
            });
        // delegated to ngOnChanges(), which includes first change
    }

    ngOnChanges(changes: SimpleChanges) {
        this.configurationService.timeConfig.pipe(
            first()
        ).subscribe(timeCfg => {
            this.init(timeCfg.time_zone);
        });
    }

    public setCustomTimeFilter(fromTime: number, toTime: number) {
        const start = moment.tz(fromTime, this.timeZone);
        const end = moment.tz(toTime, this.timeZone);
        this.customTimeRangeForm.setValue({
            startDate: start.clone().startOf('day'),
            startHour: start.hour(),
            endDate: end.clone().startOf('day'),
            endHour: end.hour()
        });
        this.updateCustomFilter(...this.mkCustomRange());
    }

    getName(type: TimeFilterType): string {
        return SETTINGS[type].name || type;
    }

    get startMinDate(): Moment {
        return this.customTimeRangeForm.controls.endDate.value ?
            this.customTimeRangeForm.controls.endDate.value.clone().startOf('day').subtract(this.boundaries.delta, 'days') :
            null;
    }

    get startMaxDate(): Moment {
        return this.customTimeRangeForm.controls.endDate.value ?
            this.customTimeRangeForm.controls.endDate.value :
            this.boundaries.maxDateTime.clone().endOf('day');
    }

    get endMinDate(): Moment {
        return this.customTimeRangeForm.controls.startDate.value ?
            this.customTimeRangeForm.controls.startDate.value :
            null;
    }

    get endMaxDate(): Moment {
        return this.customTimeRangeForm.controls.startDate.value ?
            moment.min([this.maxDate, this.customTimeRangeForm.controls.startDate.value.clone()
                .startOf('day').add(this.boundaries.delta, 'days')]) :
            this.maxDate;
    }

    get maxDate(): Moment {
        return this.boundaries.maxDateTime.clone().startOf('day');
    }

    get minHour(): number {
        return 0;
    }

    get startMaxHour(): number {
        const now = moment.tz(this.timeZone);
        return this.customTimeRangeForm.controls.startDate.value &&
        this.customTimeRangeForm.controls.startDate.value.year() === now.year() &&
        this.customTimeRangeForm.controls.startDate.value.month() === now.month() &&
        this.customTimeRangeForm.controls.startDate.value.dayOfYear() === now.dayOfYear() ?
            this.boundaries.maxDateTime.hour() : 23;
    }

    get endMaxHour(): number {
        const now = moment.tz(this.timeZone);
        return this.customTimeRangeForm.controls.endDate.value &&
        this.customTimeRangeForm.controls.endDate.value.year() === now.year() &&
        this.customTimeRangeForm.controls.endDate.value.month() === now.month() &&
        this.customTimeRangeForm.controls.endDate.value.dayOfYear() === now.dayOfYear() ?
            this.boundaries.maxDateTime.hour() : 23;
    }

    onSubmitCustomRange() {
        this.customDialogRef.close(this.mkCustomRange());
    }

    onSelectTimeFilter(event: MatSelectChange) {
        const timeFilter: TimeFilter = event.value;
        // custom time range is entirely handled by onClickTimeFilter()
        if (timeFilter.type !== TimeFilterType.CUSTOM) {
            this.timeFilter = timeFilter;
            this.timeFilterChange.emit(timeFilter);
            if (isDefined(this.keepCustomRange) && !this.keepCustomRange) {
                this.resetCustomRange();
            }
        }
    }

    onClickTimeFilter(timeFilter: TimeFilter) {
        // mat-select does not trigger selection-change events if the same option is selected twice;
        // however, for custom filters, the user might want to overwrite the range, so we employ this hack
        if (timeFilter.type === TimeFilterType.CUSTOM) {
            this.openCustomRangeDialog();
        }
    }

    private openCustomRangeDialog() {
        this.customDialogRef = this.dialog.open(this.customRangeDialog,
            {
                hasBackdrop: true,
                minWidth: `${CUSTOM_DIALOG_MIN_WIDTH}px`,
            });
        this.customDialogRef.afterClosed().subscribe(result => {
            // only update selection if the dialog was not dismissed
            if (result) {
                this.updateCustomFilter(...result);
            }
        });
    }

    private resetCustomRange() {
        this.customTimeRangeForm.setValue({
            startDate: null,
            startHour: null,
            endDate: null,
            endHour: null
        });
        this.customName = null;
    }

    private mkCustomRange(): [() => TimeRange, number, string] {
        const start = (this.customTimeRangeForm.controls.startDate.value as Moment).clone()
            .hours(this.customTimeRangeForm.controls.startHour.value);
        const end = (this.customTimeRangeForm.controls.endDate.value as Moment).clone()
            .hours(this.customTimeRangeForm.controls.endHour.value);
        const startValue = start.valueOf();
        // end hour is inclusive
        const endValue = end.valueOf() + 3600000;
        const now = moment.tz(this.timeZone).valueOf();
        return [
            () => ({
                fromTime: startValue,
                toTime: endValue,
                includesNow: now >= startValue && now <= endValue
            } as TimeRange),
            (endValue - startValue) / 60000, // range duration in minutes
            `${this.dtp.transform(start)} ÷ ${this.dtp.transform(end)}`
        ];
    }

    private validateCustomTimeRange(ctrl: AbstractControl): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
        if (ctrls.startDate.value && ctrls.endDate.value && isDefined(ctrls.startHour.value) && isDefined(ctrls.endHour.value)) {
            if (!fromBeforeTo(ctrls.startDate.value, ctrls.startHour.value, ctrls.endDate.value, ctrls.endHour.value)) {
                errs.fromdateAfterTodate = true;
            }
            // check consistency of dates and hours
            // we cannot entirely rely on min/max of <input>, as one could still enter a valid hour and *then* change the date
            if (!this.startHourInRange(ctrls.startHour.value)) {
                errs.startHourOutOfRange = true;
            }
            if (!this.endHourInRange(ctrls.endHour.value)) {
                errs.endHourOutOfRange = true;
            }
        }
        return Object.values(errs).every(err => !err) ? null : errs;
    }

    private startHourInRange(startHour: number): boolean {
        return startHour >= this.minHour && startHour <= this.startMaxHour;
    }

    private endHourInRange(endHour: number): boolean {
        return endHour >= this.minHour && endHour <= this.endMaxHour;
    }

    private updateCustomFilter(customRange: () => TimeRange, customRangeDuration: number, customRangeName: string) {
        const newCustomFilter: TimeFilter = {
            type: TimeFilterType.CUSTOM,
            duration: customRangeDuration,
            range: customRange
        };
        this.timeFilter = this.timeFilters.find(tf => tf.type === TimeFilterType.CUSTOM); // we want to keep selection reference aligned with option list
        this.customName = customRangeName;
        this.timeFilterChange.emit(newCustomFilter);
    }

    private init(timeZone: string) {
        this.timeZone = timeZone;

        // get settings for selected profile
        const profileSettings = PROFILE_SETTINGS[this.profile];
        // create (allowed) time filters
        this.timeFilters = profileSettings.types
            .filter(type => !this.allowedTypes || this.allowedTypes.includes(type))
            .map(type => SETTINGS[type].factory(this.timeZone, profileSettings.hourlyRanges));

        // set default filter

        const params = this.route.snapshot.queryParamMap;
        const timeFilterType = params.get('type') ? params.get('type') as TimeFilterType : null;
        if (this.isKeepPreviousSelectionEnabled && this.keepPreviousSelection && timeFilterType) {
            this.defaultType = timeFilterType as TimeFilterType;
            this.keepPreviousSelection = false;
        } else {
            this.defaultType = !!this.defaultType && this.defaultType !== TimeFilterType.CUSTOM ? this.defaultType : profileSettings.defaultType;
        }
        this.timeFilter = this.timeFilters.find(tf => tf.type === this.defaultType);

        // date boundaries
        this.boundaries = this.boundaries || profileSettings.boundaryFactory(this.timeZone);

        // first emission
        this.timeFilterChange.emit(this.timeFilter);
    }
}

function mkThisUnitSoFarFilter(timeZone: string, hourly: boolean, unit: 'month' | 'isoWeek' | 'day' | 'year', type: TimeFilterType): TimeFilter {
    return {
        type,
        duration: mkDuration(unit),
        range: () => {
            let now = moment.tz(timeZone);
            if (hourly) {
                now = now.startOf('hour');
            }
            return {
                fromTime: now.clone().startOf(unit).valueOf(),
                toTime: now.valueOf(),
                includesNow: !hourly
            };
        }
    };
}

function mkWholeYear(timeZone: string, hourly: boolean, unit: 'year', type: TimeFilterType): TimeFilter {
    return {
        type,
        duration: mkDuration(unit),
        range: () => {
            const boundaries = mkWholeYearBoundaries(timeZone, hourly);
            return {
                fromTime: boundaries.minDateTime.valueOf(),
                toTime: boundaries.maxDateTime.valueOf(),
                includesNow: !hourly
            };
        }
    };
}

function mkLastUnitFilter(timeZone: string, hourly: boolean, unit: 'minutes' | 'hours' | 'days', unitCount: number, type: TimeFilterType): TimeFilter {
    return {
        type,
        duration: mkDuration(unit, unitCount),
        range: () => {
            let now = moment.tz(timeZone);
            if (hourly && unit !== 'minutes') {
                now = now.startOf('hour');
            }
            return {
                fromTime: now.clone().subtract(unitCount, unit).valueOf(),
                toTime: now.valueOf(),
                includesNow: !hourly
            };
        }
    };
}

function mkYesterdayFilter(timeZone: string): TimeFilter {
    return {
        type: TimeFilterType.YESTERDAY,
        duration: mkDuration('day'),
        range: () => {
            const todayMidnight = moment.tz(timeZone).startOf('day');
            return {
                fromTime: todayMidnight.clone().subtract(1, 'days').valueOf(),
                toTime: todayMidnight.valueOf(),
                includesNow: false
            };
        }
    };
}

function mkDayBeforeYesterdayFilter(timeZone: string): TimeFilter {
    return {
        type: TimeFilterType.DAY_BEFORE_YESTERDAY,
        duration: mkDuration('day'),
        range: () => {
            const yesterdayMidnight = moment.tz(timeZone).startOf('day').subtract(1, 'days');
            return {
                fromTime: yesterdayMidnight.clone().subtract(1, 'days').valueOf(),
                toTime: yesterdayMidnight.valueOf(),
                includesNow: false
            };
        }
    };
}

function mkThisDayLastWeekFilter(timeZone: string): TimeFilter {
    return {
        type: TimeFilterType.THIS_DAY_LAST_WEEK,
        duration: mkDuration('day'),
        range: () => {
            const thisDayLastWeekMidnight = moment.tz(timeZone).startOf('day').subtract(1, 'weeks');
            return {
                fromTime: thisDayLastWeekMidnight.valueOf(),
                toTime: thisDayLastWeekMidnight.clone().add(1, 'days').valueOf(),
                includesNow: false
            };
        }
    };
}

function mkPreviousWeekFilter(timeZone: string): TimeFilter {
    return {
        type: TimeFilterType.PREVIOUS_WEEK,
        duration: mkDuration('isoWeek'),
        range: () => {
            const thisWeekStart = moment.tz(timeZone).startOf('isoWeek');
            return {
                fromTime: thisWeekStart.clone().subtract(1, 'weeks').valueOf(),
                toTime: thisWeekStart.valueOf(),
                includesNow: false
            };
        }
    };
}

function mkPreviousMonthFilter(timeZone: string, type: TimeFilterType): TimeFilter {
    return {
        type,
        duration: mkDuration('month'), // approximated as a 31-days month
        range: () => {
            const thisMonthStart = moment.tz(timeZone).startOf('month');
            return {
                fromTime: thisMonthStart.clone().subtract(1, 'months').valueOf(),
                toTime: thisMonthStart.valueOf(),
                includesNow: false
            };
        }
    };
}

function mkPreviousMonthsFilter(timeZone: string, months: number, type: TimeFilterType): TimeFilter {
    const now = moment.tz(timeZone);
    const from = now.clone().startOf('month').subtract(months - 1, 'months');
    const deltaDays = from.diff(now, 'days');
    return {
        type,
        duration: mkDuration('days', deltaDays),
        range: () => {
            return {
                fromTime: from.valueOf(),
                toTime: now.valueOf(),
                includesNow: true
            };
        }
    };
}

function mkPreviousYearFilter(timeZone: string): TimeFilter {
    return {
        type: TimeFilterType.LAST_YEAR,
        duration: mkDuration('year'),
        range: () => {
            const thisYearStart = moment.tz(timeZone).startOf('year');
            return {
                fromTime: thisYearStart.clone().subtract(1, 'years').valueOf(),
                toTime: thisYearStart.valueOf(),
                includesNow: false
            };
        }
    };
}

function mkNextUnitFilter(timeZone: string, unit: 'minutes' | 'hours' | 'days', unitCount: number, type: TimeFilterType): TimeFilter {
    return {
        type,
        duration: mkDuration(unit, unitCount),
        range: () => {
            const now = moment.tz(timeZone);
            return {
                fromTime: now.valueOf(),
                toTime: now.clone().add(unitCount, unit).valueOf(),
                includesNow: true
            };
        }
    };
}

function mkTomorrowFilter(timeZone: string): TimeFilter {
    return {
        type: TimeFilterType.TOMORROW,
        duration: mkDuration('day'),
        range: () => {
            const tomorrowMidnight = moment.tz(timeZone).startOf('day').add(1, 'days');
            return {
                fromTime: tomorrowMidnight.valueOf(),
                toTime: tomorrowMidnight.clone().add(1, 'days').valueOf(),
                includesNow: false
            };
        }
    };
}

function fromBeforeTo(startDate: Moment, startHour: number, endDate: Moment, endHour: number): boolean {
    const startDateHour: Moment = mkTime(startDate, startHour);
    const endDateHour: Moment = mkTime(endDate, endHour);
    return startDateHour.isBefore(endDateHour);
}

function mkTime(date: Moment, hour: number): Moment {
    return date.clone().hour(hour);
}

function mkWholeYearBoundaries(timeZone: string, hourly: boolean): DateBoundaries {
    let now = moment.tz(timeZone);
    if (hourly) {
        now = now.startOf('hour');
    }
    const startOfYear = now.clone().startOf('year');
    const endOfYear = now.clone().add(1, 'year').startOf('year');
    return {
        minDateTime: startOfYear,
        maxDateTime: endOfYear
    };
}

function mkDaysBoundaries(timeZone: string, lastDays: number, nextDays: number, hourly: boolean): DateBoundaries {
    let now = moment.tz(timeZone);
    if (hourly) {
        now = now.startOf('hour');
    }
    return {
        minDateTime: now.clone().startOf('day').subtract(lastDays, 'days'),
        maxDateTime: isDefined(nextDays) ? now.clone().endOf('day').add(nextDays, 'days') : now,
        delta: lastDays
    };
}

function mkDuration(unit: 'minutes' | 'hours' | 'days' | 'day' | 'isoWeek' | 'month' | 'year', duration?: number): number {
    switch (unit) {
        // "exact" values
        case 'minutes':
            return duration;
        case 'hours':
            return duration * 60;
        case 'days':
            return duration * 1440;
        // approximate values (i.e. rounded up) (e.g. "this day so far")
        case 'day':
            return 1440;
        case 'isoWeek':
            return 10080;
        case 'month':
            return 44640; // 31-days month
        case 'year':
            return 365 * 24 * 60; // 365 days year
        default:
            return 0;
    }
}
